import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  Building2,
  ChevronDown,
  ChevronRight,
  ChevronsDownUp,
  ChevronsUpDown,
  ExternalLink,
  HelpCircle,
  Info,
  Loader2,
  Network,
  Pencil,
  Plus,
  Search,
  Trash2,
  UserPlus,
  Users,
  X,
} from "lucide-react";
import { AuthContext } from "../Context/AuthContext";
import { hasRole } from "../../rbac/access";
import { resolveEmployeeIdFromProfile } from "../../components/common/employeeUtils";
import { normalizeApiError } from "../../utils/normalizeApiError";
import {
  getDirectReports,
  getEmployeeHierarchy,
  getEmployeeManager,
  getManagerChain,
  getOrgHierarchyForest,
  removeManager,
} from "./hierarchyApi";
import {
  NO_DEPARTMENT,
  buildDepartmentView,
  buildOrgView,
  departmentsOf,
  empKey,
  findTopAdmin,
  hierarchyRoleOf,
  indexForest,
  pathKeysInView,
  searchIndex,
} from "./hierarchy/hierarchyTree";
import ReportingManagerModal from "./components/ReportingManagerModal";
import ConfirmDialog from "../../components/ui/ConfirmDialog";
import { formatRoleLabel } from "./employeeRoleConstants";
import "../../styles/peopleHierarchy.css";

/** Children rendered per branch before "Show more" — keeps huge teams fast. */
const BATCH = 50;
/** Backend caps the forest depth at 20. */
const TREE_DEPTH = 20;

const AVATAR_TONES = ["blue", "green", "pink", "violet", "amber", "teal"];

function initials(name) {
  const parts = String(name || "?").trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  return (parts[0]?.slice(0, 2) || "?").toUpperCase();
}

function tone(id) {
  let h = 0;
  const s = String(id || "");
  for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) | 0;
  return AVATAR_TONES[Math.abs(h) % AVATAR_TONES.length];
}

function Avatar({ id, name, size = "md" }) {
  return (
    <span className={`ph2-avatar ph2-avatar--${size} ph2-avatar--${tone(id)}`} aria-hidden>
      {initials(name)}
    </span>
  );
}

function subLine(node) {
  return [node.employee_code, node.designation].filter(Boolean).join("  |  ");
}

/* ── Tree ───────────────────────────────────────────────────── */

/**
 * Hierarchy badge. `node.role` is the effective access role from the API (a linked
 * login account's role, else employees.role) — never the designation. An
 * Employee-role person who actually has direct reports is shown as Manager,
 * since the app allows Employee-role people to be reporting managers.
 */
function RoleBadge({ node }) {
  if (!node) return null;
  const { role: key, inferred } = hierarchyRoleOf(node);
  const reports = (node.children || []).length;
  const title = inferred
    ? `Reporting manager of ${reports} ${reports === 1 ? "person" : "people"} (access role: Employee)`
    : `Access role: ${formatRoleLabel(key)}`;
  return (
    <span className={`ph2-role ph2-role--${key}`} title={title}>
      {formatRoleLabel(key)}
    </span>
  );
}

/**
 * rowActions (admin/HR only): { hasManager(id), onEdit(node), onRemove(node) }.
 * "Remove" clears the person's reporting manager; the employee record is untouched.
 */
function TreeItem({ item, expanded, limits, selectedId, onToggle, onSelect, onShowMore, rowActions }) {
  const children = item.children || [];
  const hasChildren = children.length > 0;
  const isOpen = expanded.has(item.key);
  const limit = limits[item.key] || BATCH;
  const isGroup = item.type === "group";
  const isSelected = !isGroup && String(item.node.employee_id) === selectedId;

  return (
    <li className="ph2-tree-item">
      <div
        className={`ph2-row${isSelected ? " is-selected" : ""}${isGroup ? " ph2-row--group" : ""}`}
        data-key={item.key}
        role="treeitem"
        aria-expanded={hasChildren ? isOpen : undefined}
        aria-selected={isSelected}
        tabIndex={0}
        onClick={() => (isGroup ? onToggle(item.key) : onSelect(item.node))}
        onKeyDown={(e) => {
          if (e.key === "Enter") (isGroup ? onToggle(item.key) : onSelect(item.node));
          if (hasChildren && e.key === "ArrowRight" && !isOpen) onToggle(item.key);
          if (hasChildren && e.key === "ArrowLeft" && isOpen) onToggle(item.key);
        }}
      >
        {hasChildren ? (
          <button
            type="button"
            className="ph2-toggle"
            onClick={(e) => {
              e.stopPropagation();
              onToggle(item.key);
            }}
            aria-label={isOpen ? "Collapse" : "Expand"}
          >
            {isOpen ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
          </button>
        ) : (
          <span className="ph2-toggle ph2-toggle--empty" />
        )}

        {isGroup ? (
          <>
            <span className="ph2-group-icon" aria-hidden>
              <Building2 size={16} />
            </span>
            <span className="ph2-row-text">
              <span className="ph2-row-name">{item.label}</span>
              {item.sub ? <span className="ph2-row-sub">{item.sub}</span> : null}
            </span>
          </>
        ) : (
          <>
            <Avatar id={item.node.employee_id} name={item.node.employee_name} />
            <span className="ph2-row-text">
              <span className="ph2-row-title">
                <span className="ph2-row-name">{item.node.employee_name}</span>
                <RoleBadge node={item.node} />
              </span>
              <span className="ph2-row-sub">{subLine(item.node)}</span>
            </span>
            {rowActions ? (
              <span className="ph2-row-actions" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
                <button
                  type="button"
                  className="ph2-row-action"
                  title={rowActions.hasManager(item.node.employee_id) ? "Change reporting manager" : "Assign reporting manager"}
                  aria-label={`Edit reporting manager of ${item.node.employee_name}`}
                  onClick={() => rowActions.onEdit(item.node)}
                >
                  <Pencil size={14} />
                </button>
                {rowActions.hasManager(item.node.employee_id) ? (
                  <button
                    type="button"
                    className="ph2-row-action ph2-row-action--danger"
                    title="Remove reporting manager"
                    aria-label={`Remove reporting manager of ${item.node.employee_name}`}
                    onClick={() => rowActions.onRemove(item.node)}
                  >
                    <Trash2 size={14} />
                  </button>
                ) : null}
              </span>
            ) : null}
            {hasChildren ? (
              <span className="ph2-count" title="Direct reports">
                {children.length}
              </span>
            ) : null}
          </>
        )}
      </div>

      {hasChildren && isOpen ? (
        <ul className="ph2-tree-children" role="group">
          {children.slice(0, limit).map((child) => (
            <TreeItem
              key={child.key}
              item={child}
              expanded={expanded}
              limits={limits}
              selectedId={selectedId}
              onToggle={onToggle}
              onSelect={onSelect}
              onShowMore={onShowMore}
              rowActions={rowActions}
            />
          ))}
          {children.length > limit ? (
            <li className="ph2-tree-item">
              <button type="button" className="ph2-show-more" onClick={() => onShowMore(item.key)}>
                Show {Math.min(BATCH, children.length - limit)} more of {children.length - limit} remaining
              </button>
            </li>
          ) : null}
        </ul>
      ) : null}
    </li>
  );
}

/* ── Selected employee panel ────────────────────────────────── */

function EmployeePanel({ employee, refreshKey, canManage, onSelectId, onChangeManager, nodeOf }) {
  const employeeId = employee ? String(employee.employee_id) : null;
  const [manager, setManager] = useState(null);
  const [chain, setChain] = useState([]);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showAllReports, setShowAllReports] = useState(false);

  useEffect(() => {
    if (!employeeId) return undefined;
    let cancelled = false;
    setLoading(true);
    setError("");
    setShowAllReports(false);
    Promise.all([getEmployeeManager(employeeId), getManagerChain(employeeId), getDirectReports(employeeId)])
      .then(([mgr, chainRows, directs]) => {
        if (cancelled) return;
        setManager(mgr?.employee_id ? mgr : null);
        // API returns immediate manager first (level 1); show top-down.
        setChain((Array.isArray(chainRows) ? chainRows : []).slice().sort((a, b) => b.level - a.level));
        setReports(Array.isArray(directs) ? directs : []);
      })
      .catch((err) => !cancelled && setError(normalizeApiError(err) || "Failed to load reporting details"))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [employeeId, refreshKey]);

  if (!employee) {
    return (
      <div className="ph2-panel-empty">
        <span className="ph2-panel-empty-icon">
          <Users size={22} />
        </span>
        <p className="ph2-panel-empty-title">No employee selected</p>
        <p className="ph2-panel-empty-sub">Search for someone or click a name in the tree.</p>
      </div>
    );
  }

  const visibleReports = showAllReports ? reports : reports.slice(0, 6);

  return (
    <div className="ph2-panel-body">
      <div className="ph2-person">
        <Avatar id={employee.employee_id} name={employee.employee_name} size="lg" />
        <div className="min-w-0">
          <p className="ph2-person-name">
            {employee.employee_name} <RoleBadge node={employee} />
          </p>
          {employee.employee_code ? <p className="ph2-person-code">{employee.employee_code}</p> : null}
        </div>
      </div>

      <dl className="ph2-meta">
        <div>
          <dt>Department</dt>
          <dd>{employee.department || "—"}</dd>
        </div>
        <div>
          <dt>Designation</dt>
          <dd>{employee.designation || "—"}</dd>
        </div>
      </dl>

      {loading ? (
        <p className="ph2-loading">
          <Loader2 size={14} className="animate-spin" /> Loading reporting details…
        </p>
      ) : error ? (
        <p className="ph2-error">{error}</p>
      ) : (
        <>
          <section className="ph2-panel-section">
            <h3>Reports To</h3>
            {manager ? (
              <button type="button" className="ph2-person-link" onClick={() => onSelectId(manager.employee_id)}>
                <Avatar id={manager.employee_id} name={manager.employee_name} size="sm" />
                <span className="ph2-row-text">
                  <span className="ph2-row-title">
                    <span className="ph2-row-name">{manager.employee_name}</span>
                    <RoleBadge node={nodeOf(manager.employee_id)} />
                  </span>
                  <span className="ph2-row-sub">{manager.designation || manager.employee_code || ""}</span>
                </span>
              </button>
            ) : (
              <p className="ph2-muted">No reporting manager</p>
            )}
          </section>

          <section className="ph2-panel-section">
            <h3>Reporting Chain</h3>
            <ol className="ph2-chain">
              {chain.map((c) => (
                <li key={c.employee_id}>
                  <button type="button" className="ph2-person-link" onClick={() => onSelectId(c.employee_id)}>
                    <Avatar id={c.employee_id} name={c.employee_name} size="sm" />
                    <span className="ph2-row-text">
                      <span className="ph2-row-title">
                        <span className="ph2-row-name">{c.employee_name}</span>
                        <RoleBadge node={nodeOf(c.employee_id)} />
                      </span>
                      <span className="ph2-row-sub">{c.designation || c.employee_code || ""}</span>
                    </span>
                  </button>
                </li>
              ))}
              <li className="is-self">
                <span className="ph2-person-link">
                  <Avatar id={employee.employee_id} name={employee.employee_name} size="sm" />
                  <span className="ph2-row-text">
                    <span className="ph2-row-title">
                      <span className="ph2-row-name">{employee.employee_name}</span>
                      <RoleBadge node={employee} />
                    </span>
                    <span className="ph2-row-sub">{employee.designation || employee.employee_code || ""}</span>
                  </span>
                </span>
              </li>
            </ol>
          </section>

          <section className="ph2-panel-section">
            <h3>Direct Reports ({reports.length})</h3>
            {reports.length ? (
              <ul className="ph2-reports">
                {visibleReports.map((r) => (
                  <li key={r.employee_id}>
                    <button type="button" className="ph2-person-link" onClick={() => onSelectId(r.employee_id)}>
                      <Avatar id={r.employee_id} name={r.employee_name} size="sm" />
                      <span className="ph2-row-text">
                        <span className="ph2-row-name">{r.employee_name}</span>
                        <span className="ph2-row-sub">{[r.employee_code, r.designation].filter(Boolean).join("  |  ")}</span>
                      </span>
                    </button>
                  </li>
                ))}
                {reports.length > 6 ? (
                  <li>
                    <button type="button" className="ph2-show-more" onClick={() => setShowAllReports((v) => !v)}>
                      {showAllReports ? "Show less" : `Show all ${reports.length}`}
                    </button>
                  </li>
                ) : null}
              </ul>
            ) : (
              <p className="ph2-muted">No direct reports</p>
            )}
          </section>
        </>
      )}

      {canManage && !loading ? (
        <button type="button" className="ph2-btn ph2-btn--outline ph2-btn--block" onClick={onChangeManager}>
          {manager ? <Pencil size={15} /> : <UserPlus size={15} />}
          {manager ? "Change Reporting Manager" : "Assign Reporting Manager"}
        </button>
      ) : null}
    </div>
  );
}

/* ── Page ───────────────────────────────────────────────────── */

export default function OrgHierarchyPage() {
  const { role, user } = useContext(AuthContext);
  const canManage = hasRole(role, ["admin", "hr"]);
  const isManagerView = hasRole(role, ["manager"]) && !canManage;
  const selfId = useMemo(() => resolveEmployeeIdFromProfile(user), [user]);

  const [forest, setForest] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [view, setView] = useState("org");
  const [department, setDepartment] = useState("all");
  const [expanded, setExpanded] = useState(() => new Set());
  const [limits, setLimits] = useState({});
  const [selectedId, setSelectedId] = useState(null);
  const [panelRefresh, setPanelRefresh] = useState(0);

  const [query, setQuery] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const [activeResult, setActiveResult] = useState(0);

  const [modal, setModal] = useState({ open: false, employee: null, lock: false });

  const scrollPending = useRef(false);
  const initialised = useRef(false);
  const treeRef = useRef(null);

  const loadForest = useCallback(async () => {
    setError("");
    try {
      let data;
      if (isManagerView) {
        // Managers see their own team (org-wide forest is admin/HR only).
        data = selfId ? [await getEmployeeHierarchy(selfId, TREE_DEPTH)] : [];
      } else {
        data = await getOrgHierarchyForest(TREE_DEPTH);
      }
      setForest((Array.isArray(data) ? data : []).filter((n) => n?.employee_id));
    } catch (err) {
      setError(normalizeApiError(err) || "Failed to load the hierarchy");
    } finally {
      setLoading(false);
    }
  }, [isManagerView, selfId]);

  useEffect(() => {
    loadForest();
  }, [loadForest]);

  const index = useMemo(() => indexForest(forest), [forest]);
  const topAdmin = useMemo(() => findTopAdmin(forest), [forest]);
  const departments = useMemo(() => departmentsOf(index), [index]);

  const viewItems = useMemo(
    () => (view === "department" ? buildDepartmentView(index, department) : buildOrgView(forest)),
    [view, department, index, forest]
  );

  // Default: only the top level is open — never the whole organisation.
  useEffect(() => {
    if (initialised.current || !viewItems.length) return;
    initialised.current = true;
    // Open the top section only: the first Admin, or "Unassigned employees" when there is no Admin.
    setExpanded(new Set([viewItems[0].key]));
  }, [viewItems]);

  const selected = selectedId ? index.byId.get(selectedId)?.node || null : null;

  /** Open every branch leading to the employee and make sure its row is rendered. */
  const revealInView = useCallback((items, employeeId) => {
    const trail = pathKeysInView(items, employeeId);
    if (!trail) return false;
    setExpanded((prev) => {
      const next = new Set(prev);
      trail.forEach((it) => next.add(it.key));
      return next;
    });
    setLimits((prev) => {
      const next = { ...prev };
      const target = empKey(employeeId);
      trail.forEach((it, i) => {
        const childKey = i + 1 < trail.length ? trail[i + 1].key : target;
        const pos = (it.children || []).findIndex((c) => c.key === childKey);
        if (pos >= (next[it.key] || BATCH)) next[it.key] = Math.ceil((pos + 1) / BATCH) * BATCH;
      });
      return next;
    });
    return true;
  }, []);

  /** Select someone and locate them in the current view (switching department if needed). */
  const locate = useCallback(
    (employeeId) => {
      const id = String(employeeId);
      const entry = index.byId.get(id);
      if (!entry) {
        setSelectedId(id);
        return;
      }
      setSelectedId(id);
      scrollPending.current = true;
      if (view === "department" && department !== "all") {
        const dept = entry.node.department || NO_DEPARTMENT;
        if (dept !== department) {
          setDepartment(dept);
          revealInView(buildDepartmentView(index, dept), id);
          return;
        }
      }
      revealInView(viewItems, id);
    },
    [index, view, department, viewItems, revealInView]
  );

  // Keep the located row in view after the tree re-renders.
  useEffect(() => {
    if (!scrollPending.current || !selectedId) return;
    scrollPending.current = false;
    const id = window.requestAnimationFrame(() => {
      const el = treeRef.current?.querySelector(`[data-key="${empKey(selectedId)}"]`);
      el?.scrollIntoView({ block: "nearest", behavior: "smooth" });
    });
    return () => window.cancelAnimationFrame(id);
  }, [selectedId, expanded, limits, viewItems]);

  // Re-locate the selection when switching views so it stays visible.
  useEffect(() => {
    if (selectedId && index.byId.has(selectedId)) revealInView(viewItems, selectedId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, department]);

  const toggle = useCallback((key) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const showMore = useCallback((key) => {
    setLimits((prev) => ({ ...prev, [key]: (prev[key] || BATCH) + BATCH }));
  }, []);

  const expandAll = () => {
    const keys = new Set();
    const walk = (items) =>
      items.forEach((it) => {
        if ((it.children || []).length) {
          keys.add(it.key);
          walk(it.children);
        }
      });
    walk(viewItems);
    setExpanded(keys);
  };

  const collapseAll = () => setExpanded(new Set());

  const results = useMemo(() => searchIndex(index, query), [index, query]);

  const pickResult = (node) => {
    locate(node.employee_id);
    setQuery("");
    setSearchOpen(false);
    setActiveResult(0);
  };

  const openAssign = () => setModal({ open: true, employee: null, lock: false });
  const openChange = () => selected && setModal({ open: true, employee: selected, lock: true });

  // Row edit / remove (admin & HR). "Remove" clears the reporting manager only.
  const [removeTarget, setRemoveTarget] = useState(null);
  const [removing, setRemoving] = useState(false);
  const [removeError, setRemoveError] = useState("");

  const rowActions = useMemo(() => {
    if (!canManage) return null;
    return {
      hasManager: (id) => Boolean(index.byId.get(String(id))?.parentId),
      onEdit: (node) => {
        setSelectedId(String(node.employee_id));
        setModal({ open: true, employee: node, lock: true });
      },
      onRemove: (node) => {
        setSelectedId(String(node.employee_id));
        setRemoveError("");
        setRemoveTarget(node);
      },
    };
  }, [canManage, index]);

  const confirmRemove = async () => {
    if (!removeTarget) return;
    setRemoving(true);
    setRemoveError("");
    try {
      await removeManager(removeTarget.employee_id);
      const id = String(removeTarget.employee_id);
      setRemoveTarget(null);
      await handleSaved(id);
    } catch (err) {
      setRemoveError(normalizeApiError(err) || "Failed to remove reporting manager");
    } finally {
      setRemoving(false);
    }
  };

  const removeManagerName = removeTarget
    ? index.byId.get(index.byId.get(String(removeTarget.employee_id))?.parentId || "")?.node?.employee_name
    : null;

  const handleSaved = async (employeeId) => {
    await loadForest();
    setPanelRefresh((n) => n + 1);
    if (employeeId) {
      setSelectedId(String(employeeId));
      scrollPending.current = true;
    }
  };

  // After a reload the tree shape changes; re-open the path to the selection.
  useEffect(() => {
    if (selectedId && index.byId.has(selectedId)) revealInView(viewItems, selectedId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forest]);

  const treeTitle = view === "department" ? "Department Tree" : isManagerView ? "My Team" : "Organization Tree";

  return (
    <div className="ph2-page">
      <header className="ph2-header">
        <div>
          <h1 className="ph2-title">People Hierarchy</h1>
          <p className="ph2-subtitle">View and manage employee reporting relationships.</p>
        </div>
        <Link to="/help" className="ph2-btn ph2-btn--secondary ph2-btn--sm">
          <HelpCircle size={15} />
          Help
        </Link>
      </header>

      <details className="ph2-guide">
        <summary>
          <Info size={15} aria-hidden />
          <span>How this page works</span>
          <ChevronDown size={15} className="ph2-guide-chevron" aria-hidden />
        </summary>
        <div className="ph2-guide-body">
          <ol className="ph2-guide-steps">
            <li>
              <strong>Find a person.</strong> Type a name or employee code in the search box and pick
              them from the list. The tree opens up to that person and highlights them.
            </li>
            <li>
              <strong>Browse the structure.</strong> The Organization Tree shows who reports to whom, from
              Admin down to employees. Use <b>▸</b> to open a team, or Expand All / Collapse All.
              Switch View to <b>Department Tree</b> and pick a department to see just that team.
            </li>
            <li>
              <strong>Check the details.</strong> Click anyone to see, on the right, who they report to,
              their full reporting chain up to Admin, and who reports to them.
            </li>
            {canManage ? (
              <li>
                <strong>Change reporting lines.</strong> Use <b>+ Assign Reporting Manager</b>, or{" "}
                <b>Change Reporting Manager</b> in the details panel. Pick the manager (or tick{" "}
                <b>Report directly to Admin</b>) and Save.
              </li>
            ) : null}
          </ol>
          {canManage ? (
            <p className="ph2-guide-note">
              An employee can’t report to themselves or to someone in their own team — those choices are
              hidden. Everyone without a reporting manager is listed under “Unassigned employees” — select them to assign one.
            </p>
          ) : (
            <p className="ph2-guide-note">
              You can view your team’s reporting structure here. Ask Admin or HR to change a reporting
              manager.
            </p>
          )}
        </div>
      </details>

      <div className="ph2-toolbar">
        <div className="ph2-search">
          <Search size={16} className="ph2-search-icon" aria-hidden />
          <input
            type="search"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setSearchOpen(true);
              setActiveResult(0);
            }}
            onFocus={() => setSearchOpen(true)}
            onBlur={() => setTimeout(() => setSearchOpen(false), 150)}
            onKeyDown={(e) => {
              if (!results.length) return;
              if (e.key === "ArrowDown") {
                e.preventDefault();
                setActiveResult((i) => Math.min(i + 1, results.length - 1));
              } else if (e.key === "ArrowUp") {
                e.preventDefault();
                setActiveResult((i) => Math.max(i - 1, 0));
              } else if (e.key === "Enter") {
                e.preventDefault();
                pickResult(results[activeResult] || results[0]);
              } else if (e.key === "Escape") {
                setSearchOpen(false);
              }
            }}
            placeholder="Search employee by name or employee code…"
            role="combobox"
            aria-label="Search employee by name or employee code"
            aria-autocomplete="list"
            aria-controls="ph2-search-results"
            aria-expanded={Boolean(searchOpen && query.trim())}
            disabled={loading}
          />
          {query ? (
            <button type="button" className="ph2-search-clear" onClick={() => setQuery("")} aria-label="Clear search">
              <X size={14} />
            </button>
          ) : null}
          {searchOpen && query.trim() ? (
            <ul id="ph2-search-results" className="ph2-search-results" role="listbox">
              {results.length ? (
                results.map((n, i) => (
                  <li key={n.employee_id} role="option" aria-selected={i === activeResult}>
                    <button
                      type="button"
                      className={i === activeResult ? "is-active" : ""}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => pickResult(n)}
                    >
                      <Avatar id={n.employee_id} name={n.employee_name} size="sm" />
                      <span className="ph2-row-text">
                        <span className="ph2-row-name">{n.employee_name}</span>
                        <span className="ph2-row-sub">
                          {[n.employee_code, n.designation, n.department].filter(Boolean).join("  |  ")}
                        </span>
                      </span>
                    </button>
                  </li>
                ))
              ) : (
                <li className="ph2-search-empty">No employee matches “{query.trim()}”</li>
              )}
            </ul>
          ) : null}
        </div>

        <label className="ph2-select-field">
          <span>View</span>
          <select value={view} onChange={(e) => setView(e.target.value)}>
            <option value="org">Organization Tree</option>
            <option value="department">Department Tree</option>
          </select>
        </label>

        {view === "department" ? (
          <label className="ph2-select-field">
            <span>Department</span>
            <select value={department} onChange={(e) => setDepartment(e.target.value)}>
              <option value="all">All Departments</option>
              {departments.map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </select>
          </label>
        ) : null}

        {canManage ? (
          <button type="button" className="ph2-btn ph2-btn--primary ph2-toolbar-action" onClick={openAssign}>
            <Plus size={16} />
            Assign Reporting Manager
          </button>
        ) : null}
      </div>

      {index.conflicts.length ? (
        <div className="ph2-conflict" role="alert">
          <strong>Conflicting reporting records found.</strong> {index.conflicts.length === 1 ? "1 employee is" : `${index.conflicts.length} employees are`}{" "}
          linked to more than one manager ({index.conflicts.slice(0, 3).map((c) => c.name).join(", ")}
          {index.conflicts.length > 3 ? "…" : ""}). Each is shown once; use Change Reporting Manager to set the correct manager.
        </div>
      ) : null}

      <div className="ph2-layout">
        <section className="ph2-card ph2-tree-card" aria-label={treeTitle}>
          <div className="ph2-card-head">
            <h2>{treeTitle}</h2>
            <div className="ph2-card-actions">
              <button type="button" className="ph2-btn ph2-btn--secondary ph2-btn--sm" onClick={expandAll} disabled={!viewItems.length}>
                <ChevronsUpDown size={14} />
                Expand All
              </button>
              <button type="button" className="ph2-btn ph2-btn--secondary ph2-btn--sm" onClick={collapseAll} disabled={!viewItems.length}>
                <ChevronsDownUp size={14} />
                Collapse All
              </button>
            </div>
          </div>

          <div className="ph2-tree-scroll" ref={treeRef}>
            {loading ? (
              <p className="ph2-loading ph2-loading--center">
                <Loader2 size={16} className="animate-spin" /> Loading hierarchy…
              </p>
            ) : error ? (
              <div className="ph2-empty">
                <p>{error}</p>
                <button type="button" className="ph2-btn ph2-btn--primary ph2-btn--sm" onClick={() => { setLoading(true); loadForest(); }}>
                  Try again
                </button>
              </div>
            ) : !viewItems.length ? (
              <div className="ph2-empty">
                <Network size={24} />
                <p>No employees to show{view === "department" && department !== "all" ? " in this department" : ""}.</p>
              </div>
            ) : (
              <ul className="ph2-tree" role="tree" aria-label={treeTitle}>
                {viewItems.map((item) => (
                  <TreeItem
                    key={item.key}
                    item={item}
                    expanded={expanded}
                    limits={limits}
                    selectedId={selectedId}
                    onToggle={toggle}
                    onSelect={(node) => setSelectedId(String(node.employee_id))}
                    onShowMore={showMore}
                    rowActions={rowActions}
                  />
                ))}
              </ul>
            )}
          </div>
        </section>

        <aside className="ph2-card ph2-panel" aria-label="Employee details">
          <div className="ph2-card-head">
            <h2>Employee Details</h2>
            {selected ? (
              <Link to={`/employees/${selected.employee_id}`} className="ph2-btn ph2-btn--secondary ph2-btn--sm">
                View Profile
                <ExternalLink size={13} />
              </Link>
            ) : null}
          </div>
          <EmployeePanel
            employee={selected}
            refreshKey={panelRefresh}
            canManage={canManage}
            onSelectId={locate}
            nodeOf={(id) => index.byId.get(String(id))?.node || null}
            onChangeManager={openChange}
          />
        </aside>
      </div>

      {canManage ? (
        <ReportingManagerModal
          open={modal.open}
          initialEmployee={modal.employee}
          lockEmployee={modal.lock}
          index={index}
          topAdmin={topAdmin}
          onClose={() => setModal({ open: false, employee: null, lock: false })}
          onSaved={handleSaved}
        />
      ) : null}

      <ConfirmDialog
        open={Boolean(removeTarget)}
        title="Remove reporting manager?"
        message={
          removeError ||
          `${removeTarget?.employee_name || "This employee"} will no longer report to ${
            removeManagerName || "their manager"
          }. The employee record and their own direct reports are not affected.`
        }
        confirmLabel="Remove manager"
        onConfirm={confirmRemove}
        onCancel={() => setRemoveTarget(null)}
        loading={removing}
        danger
      />
    </div>
  );
}
